﻿import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  formatIccid,
  SimVerificationFormProps,
} from "@/components/Checkout/Steps/SimVerification/simVerificationForm";
import { useHandleError } from "@/hooks/useHandleError";
import { useInvalidBasketRecovery } from "@/hooks/useInvalidBasketRecovery";
import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  listAvailableOptions,
  useAddOption,
  useCheckSIM,
  useGetCustomer,
  useSetDeviceByUuid,
  useSetEntryCustomData,
  useSetICCID,
} from "@/services/api";
import { Basket } from "@/services/model";
import { isBusinessCustomer } from "@/utils/customerUtils";
import { getEntry } from "@/utils/dataContextHelpers";
import { getTariffType, TariffTypes } from "@/utils/deviceUtils";
import { getDeviceForProduct } from "@/utils/productMatrix";
import { getTranslatedDefaultAlias } from "@/utils/simAliasDefaults";
import {
  getFlatHomeBaseDisplayValue,
  getSelectedOptionForBasketProduct,
  isAnyFlatMobileTariff,
  isFlatHomeTariff,
  resolveSelectedOptionForRateSelection,
  transformProducts,
} from "@/utils/tariffUtils";
import {
  getStepAfterRate,
  getWizardRouteDecision,
  resolveWizardOptionId,
} from "@/utils/wizardNavigation";

/**
 * Form state, live SIM check, and physical-SIM submit/navigation used by
 * combined SimSelection step 1 and the standalone SimVerification route.
 */
export const useSimVerificationFlow = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [isPreparingNextStep, setIsPreparingNextStep] = useState(false);
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { openIfInvalidBasketError, invalidBasketModal } =
    useInvalidBasketRecovery();
  const { mutateAsync: setICCID, isPending: isSettingICCID } = useSetICCID();
  const { mutateAsync: addOption, isPending: isAddingOption } = useAddOption();
  const { mutateAsync: setDevice } = useSetDeviceByUuid();
  const { mutateAsync: setEntryCustomData } = useSetEntryCustomData();
  const { data: customerData } = useGetCustomer();
  // Only a customer with a known, non-business customerType is treated as
  // private and gets the Flat Home default alias. A pending/failed query
  // (customerData undefined) OR a customer whose customerType/id is absent
  // stays non-private, so the default is never written on an unknown type.
  // The flow still continues; checkout's DeviceDetailsForm fills the private
  // default later, and business customers name every SIM themselves.
  const isPrivate =
    customerData?.customerType?.id != null && !isBusinessCustomer(customerData);

  const {
    register,
    watch,
    resetField,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<SimVerificationFormProps>({
    mode: "onChange",
  });

  useEffect(() => {
    if (dataContext?.simVerification !== undefined) {
      if (dataContext.simVerification?.iccid !== "") {
        setValue("iccid", dataContext.simVerification.iccid, {
          shouldValidate: true,
        });
      }

      if (dataContext.simVerification?.puk !== "") {
        setValue("puk", dataContext.simVerification.puk, {
          shouldValidate: true,
        });
      }
    }
  }, [dataContext.simVerification, setValue, trigger]);

  const watchPUK = watch("puk");
  const watchIccid = watch("iccid");
  const shortenedIccid = (watchIccid && watchIccid.replaceAll(" ", "")) || "";
  const iccidIsValid = (v: string) =>
    v ? v.replaceAll(" ", "").length === 20 : false;

  const checkSIMQuery = useCheckSIM(shortenedIccid, {
    query: {
      enabled: iccidIsValid(watchIccid),
    },
  });
  useQueryCallbacks(checkSIMQuery, {
    onSuccess: (simResult) => {
      if (!simResult.puk_mandatory) {
        resetField("puk");
      }

      if (simResult?.message === "Sim is not available") {
        setError("iccid", {
          type: "iccidValidError",
        });
      } else {
        clearErrors();
      }
    },
  });
  const { data: sim, isFetching: isLoadingCheckSim } = checkSIMQuery;

  const pukMandatory = sim?.puk_mandatory === true;
  const isValid =
    iccidIsValid(watchIccid) &&
    sim?.message === "Sim is available" &&
    (!pukMandatory || watchPUK?.length === 8);

  const persistFormToContext = () => {
    setDataContext((prev) => ({
      ...prev,
      simVerification: { puk: watchPUK, iccid: watchIccid },
    }));
  };

  const saveAndNavigateBackward = async () => {
    persistFormToContext();
    navigate(portalRoutes.addDevice.simSelection);
  };

  /**
   * Writes ICCID to the basket and routes to the next wizard step.
   * Pass `basketOverride` when the caller just created/replaced the base entry
   * (combined SimSelection "Continue") so we don't rely on a stale context basket.
   */
  const saveAndNavigateForward = (basketOverride?: Basket) => {
    persistFormToContext();

    const activeBasket = basketOverride?.id
      ? basketOverride
      : dataContext.basket;

    if (!isValid || !activeBasket?.id || !sim) {
      return;
    }

    setIsPreparingNextStep(true);
    setICCID({
      basketId: activeBasket.id,
      index: 0,
      params: {
        iccid: shortenedIccid,
        clientId: 1,
        ...(pukMandatory ? { expanded: false, puk: watchPUK } : {}),
      },
    })
      .then(async (updatedBasket) => {
        const basket = updatedBasket?.id ? updatedBasket : activeBasket;
        const basketId = basket?.id;
        const entry = basket?.entries?.["0"] ?? getEntry(dataContext);

        const optionId = resolveWizardOptionId({
          simOptionId: sim.optionId,
          basketOptionId: entry?.options?.[0]?.product?.id,
          selectedOptionId: dataContext.selectedOption?.id,
          wizardEntryOptionId: dataContext.wizardEntry?.optionId,
        });

        const routeDecision = getWizardRouteDecision(optionId);
        const stepAfterRate = getStepAfterRate(
          routeDecision.includesNumberPorting,
        );
        const tariffType = getTariffType(optionId);

        const navigateForRouteDecision = () => {
          setDataContext((prev) => ({
            ...prev,
            basket,
            allowedMaxOnboardingStep: stepAfterRate.allowedMaxOnboardingStep,
          }));
          navigate(stepAfterRate.route);
        };

        const goToRateSelection = () => {
          if (dataContext.wizardEntry?.source === "purchase-intent") {
            navigateForRouteDecision();

            return;
          }

          setDataContext((prev) => ({
            ...prev,
            basket,
            allowedMaxOnboardingStep: Math.max(
              prev.allowedMaxOnboardingStep ?? 1,
              2,
            ),
          }));
          navigate(portalRoutes.addDevice.rateSelection);
        };

        if (routeDecision.isFlatHome) {
          if (!basketId || !entry?.uuid || !optionId) {
            handleError(new Error("Missing basket id / entry uuid / optionId"));

            return;
          }
          await addOption({
            basketId,
            uuid: entry.uuid,
            params: { optionId },
          })
            .then((data) => {
              if (!data.id || !entry.uuid) {
                throw new Error(
                  "Missing basket id or entry uuid after adding option",
                );
              }

              return setDevice({
                basketId: data.id,
                uuid: entry.uuid,
                deviceId: getDeviceForProduct(optionId).id,
              });
            })
            .then((data) => {
              if (!data.id || !entry.uuid) {
                throw new Error(
                  "Missing basket id or entry uuid after setting device",
                );
              }

              // Only a resolved private customer gets the Flat Home default
              // alias. Unknown (pending/failed query) and business skip it and
              // keep the device-set basket — see the isPrivate note above.
              if (!isPrivate) {
                return data;
              }

              // Single source of truth for the default: the product matrix
              // alias ("Home Internet 4G/5G"), translated to the active
              // language - same default the checkout alias field prefills.
              const flatHomeAlias = getTranslatedDefaultAlias(
                optionId,
                i18n.language,
              );

              return setEntryCustomData({
                basketId: data.id,
                uuid: entry.uuid,
                data: {
                  SimAlias: flatHomeAlias,
                },
              });
            })
            .then((data) =>
              setDataContext((prev) => ({
                ...prev,
                basket: data,
                selectedOption: {
                  id: optionId,
                  baseDisplayValue:
                    getFlatHomeBaseDisplayValue(optionId) ??
                    `Flat Home ${
                      tariffType === TariffTypes.flatHome4G ? "4G" : "5G"
                    }`,
                  isVoiceProduct: false,
                },
                allowedMaxOnboardingStep: 4,
              })),
            )
            .finally(() => navigate(portalRoutes.addDevice.step4));

          return;
        }

        if (!basketId || !entry?.uuid) {
          goToRateSelection();

          return;
        }

        const optionAlreadyOnBasket = entry.options?.[0]?.product?.id;

        if (optionId) {
          let selectedOption =
            dataContext.selectedOption?.id === optionId
              ? dataContext.selectedOption
              : ((await getSelectedOptionForBasketProduct(
                  basketId,
                  optionId,
                )) ?? dataContext.selectedOption);

          if (!selectedOption && isFlatHomeTariff(optionId)) {
            const baseDisplayValue = getFlatHomeBaseDisplayValue(optionId);

            if (baseDisplayValue) {
              selectedOption = {
                id: optionId,
                baseDisplayValue,
                isVoiceProduct: false,
              };
            }
          }

          let basketWithOption = basket;

          if (!optionAlreadyOnBasket) {
            const optionToAdd = selectedOption?.id ?? optionId;

            basketWithOption = await addOption({
              basketId,
              uuid: entry.uuid,
              params: {
                optionId: optionToAdd,
                quantity: 1,
                isONP: routeDecision.includesNumberPorting,
              },
            });

            if (!basketWithOption.id || !entry.uuid) {
              throw new Error(
                "Missing basket id or entry uuid after adding option",
              );
            }

            basketWithOption = await setDevice({
              basketId: basketWithOption.id,
              uuid: entry.uuid,
              deviceId: getDeviceForProduct(optionToAdd).id,
            });
          }

          setDataContext((prev) => ({
            ...prev,
            basket: basketWithOption,
            selectedOption: selectedOption?.id
              ? { ...selectedOption, id: optionId }
              : prev.selectedOption,
            allowedMaxOnboardingStep: stepAfterRate.allowedMaxOnboardingStep,
          }));

          navigate(stepAfterRate.route);

          return;
        }

        const availableOptions = await listAvailableOptions(basketId, 0);
        const prechargedOption = availableOptions.find(
          (option) => option.precharged,
        );
        const prechargedOptionId =
          sim.optionId ?? prechargedOption?.product?.id;

        if (!prechargedOptionId) {
          goToRateSelection();

          return;
        }

        const { processedProductsById, prechargeVoice } =
          transformProducts(availableOptions);
        const rateId = String(
          processedProductsById[prechargedOptionId]?.data ?? prechargedOptionId,
        );
        const selectedOption = resolveSelectedOptionForRateSelection({
          processedProductsById,
          currentRate: rateId,
          prechargeVoice: !!prechargeVoice,
        });

        if (!selectedOption?.id) {
          goToRateSelection();

          return;
        }

        const isPrechargedMobile = isAnyFlatMobileTariff(
          Number(prechargedOptionId),
        );
        const prechargedStep = getStepAfterRate(isPrechargedMobile);

        let basketWithOption = await addOption({
          basketId,
          uuid: entry.uuid,
          params: {
            optionId: selectedOption.id,
            quantity: 1,
            isONP: isPrechargedMobile,
          },
        });

        if (!basketWithOption.id || !entry.uuid) {
          throw new Error(
            "Missing basket id or entry uuid after adding option",
          );
        }

        basketWithOption = await setDevice({
          basketId: basketWithOption.id,
          uuid: entry.uuid,
          deviceId: getDeviceForProduct(selectedOption.id).id,
        });

        setDataContext((prev) => ({
          ...prev,
          basket: basketWithOption,
          selectedOption,
          allowedMaxOnboardingStep: prechargedStep.allowedMaxOnboardingStep,
        }));

        navigate(prechargedStep.route);
      })
      .catch((error) => {
        if (
          typeof error.response?.data === "string" &&
          error.response?.data.includes("PUK doesn't match ICCID!")
        ) {
          setError("puk", {
            message: t(
              "Onboarding.sections.sim-verification.puk.validation.rejected",
            ),
            type: "validate",
          });
        } else if (!openIfInvalidBasketError(error)) {
          handleError(error);
        }
      })
      .finally(() => {
        setIsPreparingNextStep(false);
      });
  };

  const continueIsEnabled = isValid && !isLoadingCheckSim;
  const isRoutingToNextStep =
    isSettingICCID || isAddingOption || isPreparingNextStep;

  return {
    register,
    setValue,
    errors,
    handleSubmit,
    watchIccid,
    watchPUK,
    pukMandatory,
    sim,
    isLoadingCheckSim,
    continueIsEnabled,
    isRoutingToNextStep,
    showOverlay,
    setShowOverlay,
    saveAndNavigateForward,
    saveAndNavigateBackward,
    formatIccid,
    persistFormToContext,
    invalidBasketModal,
    openIfInvalidBasketError,
  };
};
